import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Camera, MapPin, CheckCircle, Coffee, LogIn, LogOut, QrCode, RefreshCw, Navigation, Calendar, X } from 'lucide-react';
import { theme } from '../theme';
import { extractDescriptor } from '../utils/faceUtils';

// ═══════════════════════════════════════════════════════════════════
//  STAFF CHECK-IN PAGE — Mobile-first PWA
//  - Staff selects themselves (or auto via logged-in user)
//  - Actions: Check In, Check Out, Lunch Out, Lunch In
//  - Captures: selfie (front camera) + GPS + timestamp
//  - Also supports QR code scan flow
// ═══════════════════════════════════════════════════════════════════

const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' }) : '—';

const StaffCheckInPage = ({ API_BASE, currentUser }) => {
  const [staffList, setStaffList] = useState([]);
  const [staffId, setStaffId] = useState(localStorage.getItem('attendanceStaffId') || '');
  const [today, setToday] = useState(null);
  const [location, setLocation] = useState(null);
  const [locError, setLocError] = useState('');
  const [photo, setPhoto] = useState(null);           // DataURL preview
  const [photoBlob, setPhotoBlob] = useState(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [scanMode, setScanMode] = useState(false);    // QR scan in progress
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type:'', text:'' });

  // Consent-based live location tracking (OFF by default, staff toggles)
  const [liveTracking, setLiveTracking] = useState(false);
  const [liveSessionId, setLiveSessionId] = useState('');
  const [lastPingAt, setLastPingAt] = useState(null);

  // Leave application
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [myLeaves, setMyLeaves] = useState([]);
  const [leaveForm, setLeaveForm] = useState({ fromDate:'', toDate:'', leaveType:'casual', reason:'', isHalfDay:false, halfDaySlot:'first' });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanRafRef = useRef(null);
  const trackingIntervalRef = useRef(null);

  const showMsg = (type, text) => { setMessage({ type, text }); setTimeout(() => setMessage({ type:'', text:'' }), 4000); };

  // Load staff list
  useEffect(() => {
    axios.get(`${API_BASE}/staff-members`).then(r => setStaffList(r.data || []));
  }, [API_BASE]);

  // Load today's attendance
  const loadToday = useCallback(async () => {
    if (!staffId) return;
    try {
      const r = await axios.get(`${API_BASE}/attendance/today/${staffId}`);
      setToday(r.data);
    } catch {}
  }, [API_BASE, staffId]);

  useEffect(() => { loadToday(); }, [loadToday]);
  useEffect(() => {
    if (staffId) localStorage.setItem('attendanceStaffId', staffId);
  }, [staffId]);

  // Request GPS on load
  const fetchLocation = useCallback(() => {
    setLocError('');
    if (!navigator.geolocation) return setLocError('Geolocation not supported');
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => setLocError(err.message || 'Location blocked'),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 30000 }
    );
  }, []);
  useEffect(() => { fetchLocation(); }, [fetchLocation]);

  // Camera control
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; }
      setCameraOn(true);
    } catch (e) { showMsg('error', 'Camera access denied: ' + e.message); }
  };
  const stopCamera = () => {
    if (scanRafRef.current) { cancelAnimationFrame(scanRafRef.current); scanRafRef.current = null; }
    if (streamRef.current) {
      if (typeof streamRef.current._scanCancel === 'function') streamRef.current._scanCancel();
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    streamRef.current = null; setCameraOn(false); setScanMode(false);
  };
  useEffect(() => () => stopCamera(), []);

  // QR scanner — uses native BarcodeDetector if available (Chrome/Edge/Android),
  // otherwise shows a message and user falls back to manual selection.
  const startQRScan = async () => {
    if (!('BarcodeDetector' in window)) {
      return showMsg('error', 'QR scan iss browser me support nahi — manual select karo');
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; }
      setCameraOn(true); setScanMode(true);
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });

      let cancelled = false;
      const tick = async () => {
        if (cancelled || !videoRef.current || !streamRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (cancelled) return;
          if (codes.length) {
            const raw = codes[0].rawValue || '';
            const m = raw.match(/^ATT:([a-f0-9]{24})$/i);
            if (m) {
              const scannedId = m[1];
              if (staffList.some(s => s._id === scannedId)) {
                cancelled = true;
                setStaffId(scannedId);
                showMsg('success', 'Staff QR recognised — ab selfie lo');
                stopCamera();
                return;
              }
            }
            showMsg('error', 'QR code invalid — clinic ka QR scan karo');
          }
        } catch {}
        if (!cancelled) scanRafRef.current = requestAnimationFrame(tick);
      };
      // Expose cancel so stopCamera also halts this loop
      scanRafRef.current = requestAnimationFrame(tick);
      streamRef.current._scanCancel = () => { cancelled = true; };
    } catch (e) { showMsg('error', 'Camera denied: ' + e.message); }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    c.toBlob((blob) => {
      setPhotoBlob(blob);
      setPhoto(c.toDataURL('image/jpeg', 0.85));
    }, 'image/jpeg', 0.85);
    stopCamera();
  };

  const resetCapture = () => { setPhoto(null); setPhotoBlob(null); };

  // ── Live location tracking (consent-based) ─────────────────────
  const sendLivePing = useCallback(async (sessionId) => {
    if (!staffId || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const bat = await (navigator.getBattery ? navigator.getBattery() : Promise.resolve(null));
        await axios.post(`${API_BASE}/attendance/live/ping`, {
          staffId, sessionId, consent: true,
          lat: pos.coords.latitude, lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          speed: pos.coords.speed,
          battery: bat ? Math.round(bat.level * 100) : null,
        });
        setLastPingAt(new Date());
      } catch (e) { /* silent retry on next tick */ }
    }, () => {}, { enableHighAccuracy: true, timeout: 15000 });
  }, [API_BASE, staffId]);

  const startLiveTracking = () => {
    if (!staffId) return showMsg('error', 'Staff select karo pehle');
    const confirmed = window.confirm(
      'Live location tracking ON karne se aapki location har 3 minute mein clinic admin ke paas bheji jayegi (jab tak aap OFF nahi karte). Consent ke baad hi ON hoga. Proceed?'
    );
    if (!confirmed) return;
    const sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setLiveSessionId(sid);
    setLiveTracking(true);
    sendLivePing(sid);
    trackingIntervalRef.current = setInterval(() => sendLivePing(sid), 3 * 60 * 1000);
    showMsg('success', 'Live tracking ON — aapko har time pata hai ki tracking chal rahi hai');
  };

  const stopLiveTracking = () => {
    if (trackingIntervalRef.current) clearInterval(trackingIntervalRef.current);
    trackingIntervalRef.current = null;
    setLiveTracking(false);
    setLiveSessionId('');
    showMsg('info', 'Live tracking OFF — koi aur location share nahi hogi');
  };

  useEffect(() => () => { if (trackingIntervalRef.current) clearInterval(trackingIntervalRef.current); }, []);

  // ── Leave application ──
  const loadMyLeaves = useCallback(async () => {
    if (!staffId) return;
    try {
      const r = await axios.get(`${API_BASE}/leaves`, { params: { staffId, limit: 20 } });
      setMyLeaves(r.data || []);
    } catch {}
  }, [API_BASE, staffId]);

  useEffect(() => { loadMyLeaves(); }, [loadMyLeaves]);

  const submitLeave = async () => {
    if (!staffId) return showMsg('error', 'Staff select karo');
    if (!leaveForm.fromDate || !leaveForm.toDate) return showMsg('error', 'Dates fill karo');
    if (leaveForm.toDate < leaveForm.fromDate) return showMsg('error', 'To date >= From date hona chahiye');
    try {
      const fd = new FormData();
      fd.append('staffId', staffId);
      fd.append('fromDate', leaveForm.fromDate);
      fd.append('toDate', leaveForm.toDate);
      fd.append('leaveType', leaveForm.leaveType);
      fd.append('reason', leaveForm.reason);
      fd.append('isHalfDay', String(leaveForm.isHalfDay));
      if (leaveForm.isHalfDay) fd.append('halfDaySlot', leaveForm.halfDaySlot);
      await axios.post(`${API_BASE}/leaves`, fd);
      showMsg('success', 'Leave application submitted! Admin approve karega.');
      setShowLeaveForm(false);
      setLeaveForm({ fromDate:'', toDate:'', leaveType:'casual', reason:'', isHalfDay:false, halfDaySlot:'first' });
      loadMyLeaves();
    } catch (e) { showMsg('error', e?.response?.data?.error || 'Failed'); }
  };

  const cancelLeave = async (id) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try { await axios.put(`${API_BASE}/leaves/${id}/cancel`); loadMyLeaves(); }
    catch (e) { showMsg('error', e?.response?.data?.error || 'Failed'); }
  };

  // Safety: if the selected staff changes while tracking is on, force-stop —
  // otherwise the stale staffId in the interval closure keeps posting pings
  // against the previous staff.
  useEffect(() => {
    if (liveTracking) stopLiveTracking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId]);

  const submitPunch = async (action) => {
    if (!staffId) return showMsg('error', 'Staff select karo');
    if (!location) return showMsg('error', 'GPS location chahiye — allow location access');
    if (!photoBlob) return showMsg('error', 'Selfie lena zaroori hai');

    setSubmitting(true);
    try {
      // Phase 8C — compute face descriptor from selfie so server can verify
      // against registered staff face (anti-buddy-punch). Non-blocking: if no
      // face detected or models fail to load, punch still goes through.
      let faceDescriptor = null;
      if (photo) {
        try {
          const d = await extractDescriptor(photo);
          if (d) faceDescriptor = Array.from(d);
        } catch {}
      }

      const fd = new FormData();
      fd.append('staffId', staffId);
      fd.append('action', action);
      fd.append('method', 'selfie');
      fd.append('lat', location.lat);
      fd.append('lng', location.lng);
      fd.append('accuracy', location.accuracy || 0);
      fd.append('selfie', photoBlob, `selfie_${Date.now()}.jpg`);
      if (faceDescriptor) fd.append('faceDescriptor', JSON.stringify(faceDescriptor));
      const r = await axios.post(`${API_BASE}/attendance/punch`, fd);
      const faceWarn = r.data.faceMatchPassed === false
        ? ` ⚠ Face mismatch — admin will review`
        : r.data.faceMatchPassed === true ? ` ✓ Face verified` : '';
      showMsg('success', `${action.toUpperCase()} marked!${r.data.insideFence === false ? ' ⚠ Outside fence' : r.data.insideFence ? ' ✓ Inside fence' : ''}${faceWarn}`);
      setToday(r.data.attendance);
      resetCapture();
    } catch (e) { showMsg('error', e?.response?.data?.error || 'Failed to mark'); }
    setSubmitting(false);
  };

  // Determine current status to suggest next action
  const punches = today?.punches || [];
  const lastPunch = punches[punches.length - 1];
  const hasCheckedIn = punches.some(p => p.action === 'in');
  const hasCheckedOut = punches.some(p => p.action === 'out');
  const onLunch = lastPunch?.action === 'lunch-out';

  const selected = staffList.find(s => s._id === staffId);

  return (
    <div style={{ padding:'16px', maxWidth:'520px', margin:'0 auto', minHeight:'100vh', background: theme.bg }}>
      <div style={{ background:'white', borderRadius:'14px', padding:'16px', marginBottom:'12px', boxShadow: theme.shadow }}>
        <h2 style={{ color: theme.primary, margin:'0 0 4px', fontSize:'1.2rem' }}>🕒 Staff Attendance</h2>
        <div style={{ fontSize:'0.85rem', color:'#666' }}>
          {new Date().toLocaleDateString('en-IN', { weekday:'long', day:'2-digit', month:'short', year:'numeric' })}
        </div>
      </div>

      {/* Staff selector */}
      {!selected ? (
        <div style={{ background:'white', borderRadius:'14px', padding:'16px', marginBottom:'12px', boxShadow: theme.shadow }}>
          <label style={{ fontSize:'0.85rem', color:'#555', fontWeight:600 }}>Select Your Name</label>
          <select value={staffId} onChange={e => setStaffId(e.target.value)}
            style={{ width:'100%', padding:'12px', marginTop:'8px', borderRadius:'10px', border:'1px solid #ddd', fontSize:'1rem' }}>
            <option value="">— Choose staff —</option>
            {staffList.map(s => <option key={s._id} value={s._id}>{s.fullName} {s.designation ? `(${s.designation})` : ''}</option>)}
          </select>
          <div style={{ textAlign:'center', margin:'10px 0', fontSize:'0.75rem', color:'#888' }}>— or —</div>
          <button onClick={startQRScan}
            style={{ width:'100%', padding:'12px', background: theme.secondary, color:'white', border:'none', borderRadius:'10px', fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
            <QrCode size={18}/> Scan My QR Code
          </button>
        </div>
      ) : (
        <div style={{ background:'white', borderRadius:'14px', padding:'14px', marginBottom:'12px', boxShadow: theme.shadow, display:'flex', gap:'12px', alignItems:'center' }}>
          {selected.photoFile ? (
            <img src={`${API_BASE}/staff-members/${selected._id}/photo`} alt="" style={{ width:'48px', height:'48px', borderRadius:'50%', objectFit:'cover' }}/>
          ) : (
            <div style={{ width:'48px', height:'48px', borderRadius:'50%', background: theme.primaryLight, color: theme.primary, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700 }}>
              {selected.fullName.charAt(0)}
            </div>
          )}
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:600, color: theme.navy }}>{selected.fullName}</div>
            <div style={{ fontSize:'0.8rem', color:'#666' }}>{selected.designation} · {selected.department}</div>
          </div>
          <button onClick={() => { setStaffId(''); localStorage.removeItem('attendanceStaffId'); }}
            style={{ padding:'6px 10px', background:'#eee', border:'none', borderRadius:'6px', fontSize:'0.75rem', cursor:'pointer' }}>Change</button>
        </div>
      )}

      {/* QR Scanner preview (when scan mode active and no staff chosen) */}
      {scanMode && !selected && (
        <div style={{ background:'black', borderRadius:'14px', padding:'10px', marginBottom:'12px', boxShadow: theme.shadow }}>
          <video ref={videoRef} autoPlay playsInline style={{ width:'100%', borderRadius:'10px' }}/>
          <div style={{ color:'white', textAlign:'center', fontSize:'0.85rem', padding:'8px' }}>
            📷 Scanning… QR code ko camera ke saamne rakho
          </div>
          <button onClick={stopCamera} style={{ width:'100%', padding:'10px', background:'#eee', border:'none', borderRadius:'10px', cursor:'pointer' }}>Cancel</button>
        </div>
      )}

      {/* Live Tracking Banner (only when staff selected) */}
      {selected && (
        <div style={{
          background: liveTracking ? '#fef2f2' : 'white',
          border: liveTracking ? `1px solid ${theme.danger}` : '1px solid transparent',
          borderRadius:'14px', padding:'12px 14px', marginBottom:'12px', boxShadow: theme.shadow,
          display:'flex', alignItems:'center', gap:'10px',
        }}>
          <div style={{ width:'40px', height:'40px', borderRadius:'10px', background: liveTracking ? theme.danger : theme.secondaryLight, color: liveTracking ? 'white' : theme.secondary, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Navigation size={20}/>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:'0.85rem', fontWeight:600, color: theme.navy }}>
              Live Location Tracking {liveTracking && <span style={{ color: theme.danger }}>● LIVE</span>}
            </div>
            <div style={{ fontSize:'0.72rem', color:'#666', marginTop:'2px' }}>
              {liveTracking
                ? `ON — last ping ${lastPingAt ? lastPingAt.toLocaleTimeString() : 'pending'}. Aapko pata hai tracking chal rahi hai.`
                : 'Marketing staff ke liye optional. ON karne par har 3 min par location admin ko jayegi.'}
            </div>
          </div>
          {!liveTracking ? (
            <button onClick={startLiveTracking}
              style={{ padding:'8px 14px', background: theme.secondary, color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'0.8rem', fontWeight:600 }}>
              Turn ON
            </button>
          ) : (
            <button onClick={stopLiveTracking}
              style={{ padding:'8px 14px', background: theme.danger, color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'0.8rem', fontWeight:600 }}>
              Stop
            </button>
          )}
        </div>
      )}

      {/* GPS status */}
      <div style={{ background:'white', borderRadius:'14px', padding:'14px', marginBottom:'12px', boxShadow: theme.shadow, display:'flex', alignItems:'center', gap:'10px' }}>
        <div style={{ width:'40px', height:'40px', borderRadius:'10px', background: location ? `${theme.success}22` : `${theme.danger}22`, color: location ? theme.success : theme.danger, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <MapPin size={20}/>
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:'0.85rem', fontWeight:600 }}>Location</div>
          {location ? (
            <div style={{ fontSize:'0.75rem', color:'#666' }}>{location.lat.toFixed(5)}, {location.lng.toFixed(5)} (±{Math.round(location.accuracy||0)}m)</div>
          ) : (
            <div style={{ fontSize:'0.75rem', color: theme.danger }}>{locError || 'Fetching…'}</div>
          )}
        </div>
        <button onClick={fetchLocation} style={{ padding:'8px', background:'#eee', border:'none', borderRadius:'8px', cursor:'pointer' }}>
          <RefreshCw size={14}/>
        </button>
      </div>

      {/* Today's punches */}
      {today && punches.length > 0 && (
        <div style={{ background:'white', borderRadius:'14px', padding:'14px', marginBottom:'12px', boxShadow: theme.shadow }}>
          <div style={{ fontSize:'0.85rem', fontWeight:600, color: theme.navy, marginBottom:'8px' }}>Today's Punches</div>
          {punches.map((p, i) => (
            <div key={p._id || `${p.time}-${i}`} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'6px 0', fontSize:'0.85rem', borderBottom: i<punches.length-1 ? '1px solid #f1f3f5' : 'none' }}>
              <span style={{ fontWeight:600, color: theme.primary, textTransform:'uppercase', minWidth:'80px' }}>{p.action}</span>
              <span style={{ color:'#666' }}>{fmtTime(p.time)}</span>
              {p.insideFence === false && <span style={{ color: theme.danger, marginLeft:'auto', fontSize:'0.75rem' }}>⚠ Outside</span>}
              {p.insideFence === true && <span style={{ color: theme.success, marginLeft:'auto', fontSize:'0.75rem' }}>✓ Inside</span>}
            </div>
          ))}
          {today.workedMinutes > 0 && (
            <div style={{ marginTop:'10px', padding:'8px', background: theme.primaryLight, borderRadius:'8px', fontSize:'0.85rem', color: theme.navy }}>
              Worked: <b>{Math.floor(today.workedMinutes/60)}h {today.workedMinutes%60}m</b>
              {today.overtimeMinutes > 0 && <span style={{ color: theme.success }}> · +{today.overtimeMinutes}m OT</span>}
            </div>
          )}
        </div>
      )}

      {/* Camera / photo */}
      {staffId && (
        <div style={{ background:'white', borderRadius:'14px', padding:'14px', marginBottom:'12px', boxShadow: theme.shadow }}>
          <div style={{ fontSize:'0.85rem', fontWeight:600, color: theme.navy, marginBottom:'8px' }}>Selfie</div>
          {!photo && !cameraOn && (
            <button onClick={startCamera} style={{ width:'100%', padding:'20px', border:`2px dashed ${theme.border}`, borderRadius:'12px', background:'#fafbfc', cursor:'pointer' }}>
              <Camera size={32} color={theme.primary}/>
              <div style={{ marginTop:'8px', fontSize:'0.9rem', color:'#555' }}>Tap to start camera</div>
            </button>
          )}

          {cameraOn && (
            <div>
              <video ref={videoRef} autoPlay playsInline style={{ width:'100%', borderRadius:'10px', background:'#000' }}/>
              <canvas ref={canvasRef} style={{ display:'none' }}/>
              <div style={{ display:'flex', gap:'8px', marginTop:'8px' }}>
                <button onClick={capturePhoto} style={{ flex:1, padding:'12px', background: theme.primary, color:'white', border:'none', borderRadius:'10px', fontWeight:600, cursor:'pointer' }}>📸 Capture</button>
                <button onClick={stopCamera} style={{ flex:1, padding:'12px', background:'#eee', border:'none', borderRadius:'10px', cursor:'pointer' }}>Cancel</button>
              </div>
            </div>
          )}

          {photo && (
            <div>
              <img src={photo} alt="Selfie" style={{ width:'100%', borderRadius:'10px' }}/>
              <button onClick={resetCapture} style={{ width:'100%', padding:'10px', marginTop:'8px', background:'#eee', border:'none', borderRadius:'10px', cursor:'pointer', fontSize:'0.85rem' }}>Retake</button>
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      {staffId && photo && location && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'12px' }}>
          {!hasCheckedIn && (
            <ActionBtn icon={LogIn} label="Check In" color={theme.success} onClick={() => submitPunch('in')} disabled={submitting} span={2}/>
          )}
          {hasCheckedIn && !hasCheckedOut && !onLunch && (
            <>
              <ActionBtn icon={Coffee} label="Lunch Out" color={theme.warning} onClick={() => submitPunch('lunch-out')} disabled={submitting}/>
              <ActionBtn icon={LogOut} label="Check Out" color={theme.danger} onClick={() => submitPunch('out')} disabled={submitting}/>
            </>
          )}
          {onLunch && (
            <ActionBtn icon={LogIn} label="Back from Lunch" color={theme.secondary} onClick={() => submitPunch('lunch-in')} disabled={submitting} span={2}/>
          )}
          {hasCheckedOut && (
            <div style={{ gridColumn:'span 2', padding:'20px', textAlign:'center', background:'#ecfdf5', borderRadius:'12px', color: theme.success, fontWeight:600 }}>
              <CheckCircle size={28} style={{ marginBottom:'8px' }}/>
              <div>Day marked complete!</div>
            </div>
          )}
        </div>
      )}

      {/* Leave Section */}
      {selected && (
        <div style={{ background:'white', borderRadius:'14px', padding:'14px', marginBottom:'12px', boxShadow: theme.shadow }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
            <div style={{ fontSize:'0.85rem', fontWeight:600, color: theme.navy }}>📋 My Leaves</div>
            <button onClick={() => setShowLeaveForm(true)} style={{ padding:'8px 14px', background: theme.primary, color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'0.8rem', fontWeight:600 }}>
              <Calendar size={14} style={{ verticalAlign:'middle', marginRight:'4px' }}/> Apply Leave
            </button>
          </div>

          {/* Balance bar */}
          {selected.leaveBalance && (
            <div style={{ display:'flex', gap:'10px', marginBottom:'10px', padding:'10px', background:'#f0fdf4', borderRadius:'8px', borderLeft:`3px solid ${theme.success}` }}>
              <div style={{ flex:1, textAlign:'center' }}>
                <div style={{ fontSize:'1.1rem', fontWeight:700, color: theme.secondary }}>{selected.leaveBalance.casual || 0}</div>
                <div style={{ fontSize:'0.7rem', color:'#666' }}>Casual</div>
              </div>
              <div style={{ flex:1, textAlign:'center' }}>
                <div style={{ fontSize:'1.1rem', fontWeight:700, color: theme.warning }}>{selected.leaveBalance.sick || 0}</div>
                <div style={{ fontSize:'0.7rem', color:'#666' }}>Sick</div>
              </div>
              <div style={{ flex:1, textAlign:'center' }}>
                <div style={{ fontSize:'1.1rem', fontWeight:700, color: theme.success }}>{selected.leaveBalance.earned || 0}</div>
                <div style={{ fontSize:'0.7rem', color:'#666' }}>Earned</div>
              </div>
            </div>
          )}
          {myLeaves.length === 0 ? (
            <div style={{ fontSize:'0.8rem', color:'#888', padding:'10px 0' }}>No leave requests yet</div>
          ) : (
            <div style={{ maxHeight:'180px', overflowY:'auto' }}>
              {myLeaves.map(l => (
                <div key={l._id} style={{ padding:'8px 0', borderBottom:'1px solid #f1f3f5', fontSize:'0.8rem' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span><b>{l.fromDate}</b> → <b>{l.toDate}</b> ({l.totalDays}d) · {l.leaveType}</span>
                    <span style={{
                      padding:'2px 8px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:600,
                      background: l.status==='approved' ? '#dcfce7' : l.status==='rejected' ? '#fee2e2' : l.status==='cancelled' ? '#f3f4f6' : '#fef3c7',
                      color:    l.status==='approved' ? '#166534' : l.status==='rejected' ? '#991b1b' : l.status==='cancelled' ? '#374151' : '#92400e',
                    }}>{l.status}</span>
                  </div>
                  {l.reason && <div style={{ color:'#666', fontSize:'0.75rem', marginTop:'2px' }}>{l.reason}</div>}
                  {l.status === 'pending' && (
                    <button onClick={() => cancelLeave(l._id)} style={{ marginTop:'4px', padding:'3px 8px', background:'transparent', color: theme.danger, border:'1px solid', borderColor: theme.danger, borderRadius:'6px', fontSize:'0.7rem', cursor:'pointer' }}>Cancel</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Leave Form Modal */}
      {showLeaveForm && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:9999, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
          onClick={() => setShowLeaveForm(false)}>
          <div style={{ background:'white', borderRadius:'14px 14px 0 0', padding:'20px', width:'100%', maxWidth:'520px', maxHeight:'90vh', overflowY:'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
              <h3 style={{ margin:0, color: theme.navy }}>Apply for Leave</h3>
              <button onClick={() => setShowLeaveForm(false)} style={{ background:'none', border:'none', cursor:'pointer' }}><X size={20}/></button>
            </div>

            <label style={{ fontSize:'0.85rem', fontWeight:600 }}>Type</label>
            <select value={leaveForm.leaveType} onChange={e => setLeaveForm({ ...leaveForm, leaveType: e.target.value })}
              style={{ width:'100%', padding:'10px', marginTop:'4px', borderRadius:'8px', border:'1px solid #ddd' }}>
              <option value="casual">Casual Leave (CL)</option>
              <option value="sick">Sick Leave (SL)</option>
              <option value="earned">Earned Leave (EL)</option>
              <option value="comp-off">Comp Off</option>
              <option value="unpaid">Unpaid Leave</option>
              <option value="maternity">Maternity</option>
              <option value="other">Other</option>
            </select>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginTop:'10px' }}>
              <div>
                <label style={{ fontSize:'0.85rem', fontWeight:600 }}>From</label>
                <input type="date" value={leaveForm.fromDate} onChange={e => setLeaveForm({ ...leaveForm, fromDate: e.target.value })}
                  style={{ width:'100%', padding:'10px', marginTop:'4px', borderRadius:'8px', border:'1px solid #ddd' }}/>
              </div>
              <div>
                <label style={{ fontSize:'0.85rem', fontWeight:600 }}>To</label>
                <input type="date" value={leaveForm.toDate} onChange={e => setLeaveForm({ ...leaveForm, toDate: e.target.value })}
                  style={{ width:'100%', padding:'10px', marginTop:'4px', borderRadius:'8px', border:'1px solid #ddd' }}/>
              </div>
            </div>

            <label style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:'10px', fontSize:'0.85rem' }}>
              <input type="checkbox" checked={leaveForm.isHalfDay}
                onChange={e => setLeaveForm({ ...leaveForm, isHalfDay: e.target.checked, toDate: e.target.checked ? leaveForm.fromDate : leaveForm.toDate })}/>
              Half Day
            </label>

            {leaveForm.isHalfDay && (
              <select value={leaveForm.halfDaySlot} onChange={e => setLeaveForm({ ...leaveForm, halfDaySlot: e.target.value })}
                style={{ width:'100%', padding:'10px', marginTop:'8px', borderRadius:'8px', border:'1px solid #ddd' }}>
                <option value="first">First Half</option>
                <option value="second">Second Half</option>
              </select>
            )}

            <label style={{ fontSize:'0.85rem', fontWeight:600, display:'block', marginTop:'10px' }}>Reason</label>
            <textarea value={leaveForm.reason} onChange={e => setLeaveForm({ ...leaveForm, reason: e.target.value })}
              rows={3} placeholder="Why are you taking leave?"
              style={{ width:'100%', padding:'10px', marginTop:'4px', borderRadius:'8px', border:'1px solid #ddd', fontFamily:'inherit', resize:'vertical' }}/>

            <button onClick={submitLeave}
              style={{ width:'100%', padding:'14px', marginTop:'14px', background: theme.primary, color:'white', border:'none', borderRadius:'10px', fontWeight:600, fontSize:'1rem', cursor:'pointer' }}>
              Submit Application
            </button>
          </div>
        </div>
      )}

      {/* Message */}
      {message.text && (
        <div style={{
          position:'fixed', bottom:'20px', left:'20px', right:'20px', maxWidth:'480px', margin:'0 auto',
          padding:'12px 16px', borderRadius:'10px', boxShadow: theme.shadowLg,
          background: message.type === 'success' ? '#dcfce7' : message.type === 'error' ? '#fee2e2' : '#e0e7ff',
          color: message.type === 'success' ? '#166534' : message.type === 'error' ? '#991b1b' : '#3730a3',
          fontSize:'0.9rem', fontWeight:500,
        }}>{message.text}</div>
      )}
    </div>
  );
};

const ActionBtn = ({ icon: Icon, label, color, onClick, disabled, span }) => (
  <button onClick={onClick} disabled={disabled}
    style={{
      gridColumn: span === 2 ? 'span 2' : undefined,
      padding:'18px', background: color, color:'white', border:'none', borderRadius:'14px',
      fontSize:'1rem', fontWeight:600, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1, display:'flex', flexDirection:'column', alignItems:'center', gap:'6px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    }}>
    <Icon size={24}/>
    <span>{label}</span>
  </button>
);

export default StaffCheckInPage;
